import {
  Claim,
  CommissionLine,
  CommissionSettlement,
  Communication,
  Endorsement,
  Installment,
  IssuanceRequest,
  Person,
  Policy,
  PolicyDocument,
  ProtoDb,
  Quote,
  Ticket,
  TreeMember,
  UnusualReport,
  WizardDraft,
} from '../models/domain.models';
import {
  CLAIM_DEADLINE_WARN_DAYS,
  COMMISSION_TOLERANCE,
  CLAIM_STALE_NEWS_DAYS,
  MORA_DAYS,
  OFFER_EXPIRING_DAYS,
  OFFER_VALID_DAYS,
  SYNC_STALE_DAYS,
} from '../models/rules';
import {
  ClaimRow,
  CommissionLineRow,
  CommissionSummary,
  CommissionSummaryRow,
  CommunicationRow,
  DocumentRow,
  EndorsementRow,
  InstallmentRow,
  InstallmentsSummary,
  IssuanceRequestRow,
  PersonRow,
  PolicyRow,
  QuoteRow,
  RenewalContext,
  RenewalState,
  SettlementRow,
  TicketRow,
  UnusualReportRow,
} from '../models/views.models';
import { SessionUser } from '../session/session.models';
import { agreementAt, lineExpected, subtreeOids } from './commission-math';
import { documentAssociations } from './document-links';
import { addDays, daysBetween } from './rng';

export interface ProjectionContext {
  db: ProtoDb;
  today: string;
  user: SessionUser | null;
  persons: Map<string, Person>;
  policies: Map<string, Policy>;
  claims: Map<string, Claim>;
  quotes: Map<string, Quote>;
  members: Map<string, TreeMember>;
  companies: Map<string, string>;
  installmentsByPolicy: Map<string, Installment[]>;
  renewedBy: Map<string, Policy>;
  renewalQuotes: Map<string, Quote>;
  endorsements: Map<string, Endorsement>;
  subtrees: Map<string, Set<string>>;
}

const OPEN_CLAIM = ['reported', 'documentation', 'in-analysis'];
const ACTIVE_TICKET = ['open', 'in-progress', 'waiting-company'];

function indexBy<T extends { oid: string }>(items: T[]): Map<string, T> {
  return new Map(items.map(i => [i.oid, i]));
}

export function buildContext(db: ProtoDb, today: string, user: SessionUser | null): ProjectionContext {
  const installmentsByPolicy = new Map<string, Installment[]>();
  for (const i of db.installments) {
    const list = installmentsByPolicy.get(i.policyOid);
    if (list) list.push(i);
    else installmentsByPolicy.set(i.policyOid, [i]);
  }
  const renewalQuotes = new Map<string, Quote>();
  for (const q of db.quotes) {
    if (q.renewalOfPolicyOid && q.status !== 'desisted' && !renewalQuotes.has(q.renewalOfPolicyOid)) {
      renewalQuotes.set(q.renewalOfPolicyOid, q);
    }
  }
  return {
    db, today, user,
    persons: indexBy(db.persons), policies: indexBy(db.policies), claims: indexBy(db.claims),
    quotes: indexBy(db.quotes), members: indexBy(db.members),
    companies: new Map(db.companies.map(c => [c.oid, c.name])),
    installmentsByPolicy,
    renewedBy: new Map(db.policies.filter(p => p.renewalOfOid).map(p => [p.renewalOfOid as string, p])),
    renewalQuotes,
    endorsements: indexBy(db.endorsements),
    subtrees: new Map(),
  };
}

function memberName(ctx: ProjectionContext, oid: string): string {
  return ctx.members.get(oid)?.name ?? '—';
}

function personRow(p: Person, ctx: ProjectionContext): PersonRow {
  const { db, today } = ctx;
  const policies = db.policies.filter(x => x.personOid === p.oid);
  const active = policies.filter(x => x.status === 'active');
  const claims = db.claims.filter(c => c.personOid === p.oid);
  const withAmount = claims.filter(c => c.settledAmount !== null);
  const overdue = active.reduce(
    (sum, x) => sum + (ctx.installmentsByPolicy.get(x.oid) ?? []).filter(i => i.status === 'overdue').length, 0);
  const contacts = [
    p.createDate,
    ...db.tickets.filter(t => t.personOid === p.oid).map(t => t.lastActivityDate),
    ...db.quotes.filter(q => q.personOid === p.oid).map(q => q.createDate),
    ...db.communications.filter(c => c.personOid === p.oid).map(c => c.date),
  ];
  return {
    ...p,
    ownerName: memberName(ctx, p.ownerOid),
    lines: [...new Set(active.map(x => x.lineOfBusiness))],
    totalPolicies: policies.length,
    activePolicies: active.length,
    expiringPolicies: active.filter(x => x.endDate >= today && x.endDate <= addDays(today, 30)).length,
    annualPremium: active.reduce((sum, x) => sum + x.premium, 0),
    openClaims: claims.filter(c => OPEN_CLAIM.includes(c.status)).length,
    totalClaims: claims.length,
    claimsWithAmount: withAmount.length,
    settledAmount: withAmount.reduce((sum, c) => sum + (c.settledAmount ?? 0), 0),
    overdueInstallments: overdue,
    activePoliciesWithInstallments: active.filter(x => x.installmentsLoaded).length,
    lastContactDate: contacts.reduce((a, b) => (a > b ? a : b)),
  };
}

function renewalStateOf(p: Policy, ctx: ProjectionContext): RenewalState {
  const next = ctx.renewedBy.get(p.oid);
  if (next) return next.companyOid === p.companyOid ? 'renewed-same' : 'renewed-other';
  if (p.notRenewing) return 'not-renewing';
  const quote = ctx.renewalQuotes.get(p.oid);
  if (!quote) return 'unmanaged';
  return ['presented', 'chosen-by-client', 'accepted'].includes(quote.status) ? 'presented' : 'quoted';
}

function clientClaimsOf(personOid: string, ctx: ProjectionContext): { count: number; amount: number; open: number } {
  const claims = ctx.db.claims.filter(c => c.personOid === personOid);
  return {
    count: claims.length,
    amount: claims.reduce((sum, c) => sum + (c.settledAmount ?? 0), 0),
    open: claims.filter(c => OPEN_CLAIM.includes(c.status)).length,
  };
}

function policyRow(p: Policy, ctx: ProjectionContext): PolicyRow {
  const threshold = SYNC_STALE_DAYS[p.lineOfBusiness];
  const age = daysBetween(p.lastSyncDate, ctx.today);
  const claims = clientClaimsOf(p.personOid, ctx);
  return {
    ...p,
    clientClaims: claims.count,
    clientClaimsAmount: claims.amount,
    renewalState: renewalStateOf(p, ctx),
    renewalQuoteOid: ctx.renewalQuotes.get(p.oid)?.oid ?? null,
    renewalOfNumber: p.renewalOfOid ? ctx.policies.get(p.renewalOfOid)?.number ?? null : null,
    personName: ctx.persons.get(p.personOid)?.displayName ?? '—',
    ownerName: memberName(ctx, p.ownerOid),
    companyName: ctx.companies.get(p.companyOid) ?? '—',
    overdueInstallments: (ctx.installmentsByPolicy.get(p.oid) ?? []).filter(i => i.status === 'overdue').length,
    openClaims: ctx.db.claims.filter(c => c.policyOid === p.oid && OPEN_CLAIM.includes(c.status)).length,
    staleSync: age > threshold,
    syncAgeDays: age,
    staleThresholdDays: threshold,
    nextOfOid: ctx.renewedBy.get(p.oid)?.oid ?? null,
    licenseHolderName: p.licenseHolderOid ? memberName(ctx, p.licenseHolderOid) : null,
    licenseNumber: p.licenseHolderOid ? ctx.members.get(p.licenseHolderOid)?.license ?? null : null,
    attributedToName: p.attributedToOid ? memberName(ctx, p.attributedToOid) : null,
  };
}

export function offersValidUntil(q: Quote): string | null {
  const responded = q.offers.map(o => o.respondedAt).filter((d): d is string => !!d);
  return responded.length ? addDays(responded.reduce((a, b) => (a > b ? a : b)), OFFER_VALID_DAYS) : null;
}

function renewalContext(q: Quote, ctx: ProjectionContext): RenewalContext | null {
  const policy = q.renewalOfPolicyOid ? ctx.policies.get(q.renewalOfPolicyOid) : undefined;
  if (!policy) return null;
  const claims = clientClaimsOf(policy.personOid, ctx);
  return {
    policyOid: policy.oid, policyNumber: policy.number, companyOid: policy.companyOid,
    companyName: ctx.companies.get(policy.companyOid) ?? '—', premium: policy.premium, endDate: policy.endDate,
    clientClaims: claims.count, clientClaimsAmount: claims.amount, openClaims: claims.open,
  };
}

function quoteRow(q: Quote, ctx: ProjectionContext): QuoteRow {
  const received = q.offers.filter(o => o.status === 'received');
  const validUntil = offersValidUntil(q);
  const premiums = received.map(o => o.premium ?? 0).filter(v => v > 0);
  const ticket = ctx.db.tickets.find(t => t.quoteOid === q.oid);
  return {
    ...q,
    personName: ctx.persons.get(q.personOid)?.displayName ?? '—',
    ownerName: memberName(ctx, q.ownerOid),
    riskSummary: String(q.risk['vehicle'] ?? q.risk['description'] ?? '—'),
    requestedOffers: q.offers.length,
    receivedOffers: received.length,
    bestPremium: premiums.length ? Math.min(...premiums) : null,
    validUntil,
    displayStatus: q.status === 'presented' && validUntil !== null && validUntil < ctx.today ? 'expired' : q.status,
    saleTicketOid: ticket?.oid ?? null,
    saleTicketNumber: ticket?.number ?? null,
    saleTicketAssigneeName: ticket ? memberName(ctx, ticket.assigneeOid) : null,
    saleTicketAssigneeOid: ticket?.assigneeOid ?? null,
    renewalOf: renewalContext(q, ctx),
  };
}

function claimRow(c: Claim, ctx: ProjectionContext): ClaimRow {
  const policy = ctx.policies.get(c.policyOid);
  return {
    ...c,
    policyNumber: policy?.number ?? '—',
    lineOfBusiness: policy?.lineOfBusiness ?? 'auto',
    personName: ctx.persons.get(c.personOid)?.displayName ?? '—',
    companyName: ctx.companies.get(c.companyOid) ?? '—',
    ownerName: memberName(ctx, c.ownerOid),
    open: OPEN_CLAIM.includes(c.status),
    missingDocuments: c.checklist.filter(item => !item.received).length,
    daysWithoutNews: Math.max(0, daysBetween(c.lastUpdateDate, ctx.today)),
  };
}

function ticketRelated(t: Ticket, ctx: ProjectionContext): { label: string | null; companyOid: string | null } {
  switch (t.relatedType) {
    case 'policy': {
      const p = ctx.policies.get(t.relatedOid ?? '');
      return { label: p ? `Póliza ${p.number}` : null, companyOid: p?.companyOid ?? null };
    }
    case 'claim': {
      const c = ctx.claims.get(t.relatedOid ?? '');
      return { label: c ? `Siniestro ${c.number}` : null, companyOid: c?.companyOid ?? null };
    }
    case 'quote': {
      const q = ctx.quotes.get(t.relatedOid ?? '');
      return { label: q ? `Cotización ${q.number}` : null, companyOid: null };
    }
    case 'person':
      return { label: ctx.persons.get(t.relatedOid ?? '')?.displayName ?? null, companyOid: null };
    case 'settlement': {
      const s = ctx.db.settlements.find(x => x.oid === t.relatedOid);
      return { label: s ? `Liquidación ${s.period}` : null, companyOid: s?.companyOid ?? null };
    }
    default:
      return { label: null, companyOid: null };
  }
}

function ticketRow(t: Ticket, ctx: ProjectionContext): TicketRow {
  const related = ticketRelated(t, ctx);
  const active = ACTIVE_TICKET.includes(t.status);
  return {
    ...t,
    personName: t.personOid ? ctx.persons.get(t.personOid)?.displayName ?? null : null,
    assigneeName: memberName(ctx, t.assigneeOid),
    creatorName: memberName(ctx, t.creatorOid),
    companyOid: related.companyOid,
    companyName: related.companyOid ? ctx.companies.get(related.companyOid) ?? null : null,
    relatedLabel: related.label,
    quoteNumber: t.quoteOid ? ctx.quotes.get(t.quoteOid)?.number ?? null : null,
    active,
    overdue: active && t.dueDate !== null && t.dueDate < ctx.today,
  };
}

function installmentRow(i: Installment, ctx: ProjectionContext): InstallmentRow {
  const policy = ctx.policies.get(i.policyOid);
  const daysLate = i.status === 'paid' ? 0 : Math.max(0, daysBetween(i.dueDate, ctx.today));
  return {
    ...i,
    policyNumber: policy?.number ?? '—',
    personOid: policy?.personOid ?? '',
    personName: ctx.persons.get(policy?.personOid ?? '')?.displayName ?? '—',
    companyOid: policy?.companyOid ?? '',
    companyName: ctx.companies.get(policy?.companyOid ?? '') ?? '—',
    lineOfBusiness: policy?.lineOfBusiness ?? 'auto',
    totalInstallments: ctx.installmentsByPolicy.get(i.policyOid)?.length ?? 0,
    displayStatus: i.status === 'overdue' && daysLate > MORA_DAYS ? 'mora' : i.status,
    daysLate,
  };
}

function issuanceRow(r: IssuanceRequest, ctx: ProjectionContext): IssuanceRequestRow {
  const quote = ctx.quotes.get(r.quoteOid);
  return {
    ...r,
    quoteNumber: quote?.number ?? '—',
    personOid: quote?.personOid ?? '',
    personName: ctx.persons.get(quote?.personOid ?? '')?.displayName ?? '—',
    companyName: ctx.companies.get(r.companyOid) ?? '—',
    lineOfBusiness: quote?.lineOfBusiness ?? 'auto',
    requestedByName: memberName(ctx, r.requestedByOid),
    licenseHolderName: memberName(ctx, r.licenseHolderOid),
    attributedToName: memberName(ctx, r.ownerOid),
    policyNumber: r.policyOid ? ctx.policies.get(r.policyOid)?.number ?? null : null,
    final: r.status === 'issued' || r.status === 'rejected',
  };
}

function endorsementRow(e: Endorsement, ctx: ProjectionContext): EndorsementRow {
  const policy = ctx.policies.get(e.policyOid);
  const ticket = e.ticketOid ? ctx.db.tickets.find(t => t.oid === e.ticketOid) : undefined;
  return {
    ...e,
    policyNumber: policy?.number ?? '—',
    lineOfBusiness: policy?.lineOfBusiness ?? 'auto',
    personOid: policy?.personOid ?? '',
    personName: ctx.persons.get(policy?.personOid ?? '')?.displayName ?? '—',
    companyOid: policy?.companyOid ?? '',
    companyName: ctx.companies.get(policy?.companyOid ?? '') ?? '—',
    ownerName: memberName(ctx, e.ownerOid),
    retroactive: e.effectDate < e.registerDate,
    ticketNumber: ticket?.number ?? null,
    documentName: e.documentOid ? ctx.db.documents.find(d => d.oid === e.documentOid)?.name ?? null : null,
  };
}

function communicationRow(c: Communication, ctx: ProjectionContext): CommunicationRow {
  return {
    ...c,
    personName: ctx.persons.get(c.personOid)?.displayName ?? '—',
    sentByName: memberName(ctx, c.sentByOid),
  };
}

const DOCUMENT_SOURCE_NAME: Record<PolicyDocument['origin'], string> = {
  generated: 'Plataforma', 'from-company': 'Compañía', uploaded: '—', 'client-supplied': 'Cliente',
};

function documentRow(d: PolicyDocument, ctx: ProjectionContext): DocumentRow {
  const associations = documentAssociations(d, ctx.db);
  return {
    ...d,
    personName: ctx.persons.get(d.personOid)?.displayName ?? '—',
    uploadedByName: d.uploadedByOid ? memberName(ctx, d.uploadedByOid) : DOCUMENT_SOURCE_NAME[d.origin],
    associations,
    entityTypes: [...new Set(associations.map(a => a.type))],
  };
}

function unusualReportRow(r: UnusualReport, ctx: ProjectionContext): UnusualReportRow {
  return {
    ...r,
    personName: ctx.persons.get(r.personOid)?.displayName ?? '—',
    policyNumbers: r.policyOids.map(oid => ctx.policies.get(oid)?.number ?? '—'),
  };
}

/** A saved P-31 wizard shown in P-30 as a quote in state "borrador"; it is not a `Quote` and nothing else counts it. */
export function draftQuoteRow(d: WizardDraft, ctx: ProjectionContext): QuoteRow {
  const person = d.personOid ? ctx.persons.get(d.personOid) : undefined;
  return {
    oid: d.oid, number: 'Borrador', lineOfBusiness: (d.data['line'] as Quote['lineOfBusiness']) ?? 'auto', personOid: d.personOid ?? '',
    ownerOid: d.ownerOid, status: 'draft', createDate: d.updateDate, risk: {}, offers: [], version: 1, previousVersions: [],
    chosenCompanyOid: null, share: null, clientChoice: null, desistReason: null,
    personName: person?.displayName ?? String(d.data['personLabel'] ?? 'Sin persona elegida'),
    ownerName: memberName(ctx, d.ownerOid), riskSummary: d.label, requestedOffers: 0, receivedOffers: 0, bestPremium: null,
    validUntil: null, displayStatus: 'draft', saleTicketOid: null, saleTicketNumber: null, saleTicketAssigneeName: null,
    saleTicketAssigneeOid: null, renewalOf: null, draftOid: d.oid,
  };
}

function subtreeOf(ctx: ProjectionContext, oid: string): Set<string> {
  let found = ctx.subtrees.get(oid);
  if (!found) {
    found = subtreeOids(ctx.db.members, oid);
    ctx.subtrees.set(oid, found);
  }
  return found;
}

function commissionLineRow(l: CommissionLine, ctx: ProjectionContext): CommissionLineRow {
  const policy = ctx.policies.get(l.policyOid);
  const expected = lineExpected(ctx.db.agreements, l);
  const ratePct = agreementAt(ctx.db.agreements, l.holderOid, l.companyOid, l.lineOfBusiness, l.paidDate)?.ratePct ?? null;
  const difference = l.settled !== null && expected !== null ? l.settled - expected : null;
  return {
    ...l,
    policyNumber: policy?.number ?? '—',
    personName: ctx.persons.get(policy?.personOid ?? '')?.displayName ?? '—',
    companyName: ctx.companies.get(l.companyOid) ?? '—',
    ownerName: memberName(ctx, l.ownerOid),
    ratePct, expected, difference,
    state: l.settlementOid === null ? 'accrued' : l.settled === null ? 'no-detail' : 'settled',
    claimDue: difference !== null && difference <= -COMMISSION_TOLERANCE,
    belowTolerance: difference !== null && difference !== 0 && Math.abs(difference) < COMMISSION_TOLERANCE,
    ticketNumber: l.ticketOid ? ctx.db.tickets.find(t => t.oid === l.ticketOid)?.number ?? null : null,
  };
}

function settlementRow(s: CommissionSettlement, ctx: ProjectionContext): SettlementRow {
  const lines = ctx.db.commissionLines.filter(l => l.settlementOid === s.oid).map(l => commissionLineRow(l, ctx));
  const withDifferences = lines.some(l => l.claimDue || l.ticketOid !== null);
  return {
    ...s,
    companyName: ctx.companies.get(s.companyOid) ?? '—',
    lineCount: lines.length,
    detailTotal: lines.reduce((sum, l) => sum + (l.settled ?? 0), 0),
    claimCount: lines.filter(l => l.ticketOid !== null).length,
    processing: s.unmatched > 0 ? 'with-unmatched' : withDifferences ? 'with-differences' : 'processed',
  };
}

export function summarizeCommissions(rows: CommissionLineRow[]): CommissionSummary {
  const groups = new Map<string, CommissionSummaryRow>();
  for (const r of rows) {
    const key = `${r.period}|${r.companyOid}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        period: r.period, companyOid: r.companyOid, companyName: r.companyName, expected: 0, settled: 0, difference: 0,
        accrued: 0, lines: 0, withoutAgreement: 0, withoutDetail: 0, claims: 0,
      };
      groups.set(key, g);
    }
    g.lines++;
    if (r.expected === null) g.withoutAgreement++;
    if (r.state === 'accrued') g.accrued += r.expected ?? 0;
    else if (r.state === 'no-detail') g.withoutDetail++;
    else {
      g.settled += r.settled ?? 0;
      if (r.expected !== null) {
        g.expected += r.expected;
        g.difference += r.difference ?? 0;
      }
    }
    if (r.ticketOid !== null) g.claims++;
  }
  const out = [...groups.values()].sort((a, b) => b.period.localeCompare(a.period) || a.companyName.localeCompare(b.companyName));
  const total = (pick: (g: CommissionSummaryRow) => number) => out.reduce((sum, g) => sum + pick(g), 0);
  return {
    expected: total(g => g.expected), settled: total(g => g.settled), difference: total(g => g.difference),
    accrued: total(g => g.accrued), lines: total(g => g.lines), withoutAgreement: total(g => g.withoutAgreement),
    withoutDetail: total(g => g.withoutDetail), claims: total(g => g.claims), rows: out,
  };
}

export function projectRow(name: string, item: unknown, ctx: ProjectionContext): unknown {
  switch (name) {
    case 'persons': return personRow(item as Person, ctx);
    case 'policies': return policyRow(item as Policy, ctx);
    case 'quotes': return quoteRow(item as Quote, ctx);
    case 'claims': return claimRow(item as Claim, ctx);
    case 'tickets': return ticketRow(item as Ticket, ctx);
    case 'installments': return installmentRow(item as Installment, ctx);
    case 'issuance-requests': return issuanceRow(item as IssuanceRequest, ctx);
    case 'endorsements': return endorsementRow(item as Endorsement, ctx);
    case 'communications': return communicationRow(item as Communication, ctx);
    case 'documents': return documentRow(item as PolicyDocument, ctx);
    case 'unusual-reports': return unusualReportRow(item as UnusualReport, ctx);
    case 'commission-lines': return commissionLineRow(item as CommissionLine, ctx);
    case 'settlements': return settlementRow(item as CommissionSettlement, ctx);
    default: return item;
  }
}

type Row = Record<string, unknown>;
type Predicate = (row: Row, value: string, ctx: ProjectionContext) => boolean;

/** A quote the client chose that nobody has written to since (communications are the contact record). */
export function isUncontactedChoice(q: Quote, db: ProtoDb): boolean {
  if (q.status !== 'chosen-by-client') return false;
  const since = q.clientChoice?.date ?? q.createDate;
  return !db.communications.some(c => c.personOid === q.personOid && c.date >= since);
}

/** Funnel stages of the dashboard: each one counts the quotes that reached it. */
export const QUOTE_STAGES: Record<string, string[]> = {
  presented: ['presented', 'chosen-by-client', 'accepted', 'issued'],
  accepted: ['accepted', 'issued'],
  issued: ['issued'],
};

const truthy = (test: (row: Row, ctx: ProjectionContext) => boolean): Predicate =>
  (row, value, ctx) => value !== 'true' || test(row, ctx);

const PREDICATES: Record<string, Record<string, Predicate>> = {
  persons: {
    line: (r, v) => (r['lines'] as string[]).includes(v),
    renewing: truthy(r => (r['expiringPolicies'] as number) > 0),
    overdue: truthy(r => (r['overdueInstallments'] as number) > 0),
  },
  policies: {
    expiring: (r, v, ctx) => r['status'] === 'active' && String(r['endDate']) >= ctx.today
      && String(r['endDate']) <= addDays(ctx.today, Number(v)),
    renewal: (r, v) => (r['renewalOfOid'] !== null) === (v === 'yes'),
    divergent: truthy(r => r['divergent'] === true),
    stale: truthy(r => r['staleSync'] === true),
    overdue: truthy(r => (r['overdueInstallments'] as number) > 0),
  },
  quotes: {
    status: (r, v) => r['displayStatus'] === v,
    stage: (r, v) => (QUOTE_STAGES[v] ?? []).includes(String(r['status'])),
    expiring: truthy((r, ctx) => r['displayStatus'] === 'presented' && r['validUntil'] !== null
      && String(r['validUntil']) <= addDays(ctx.today, OFFER_EXPIRING_DAYS)),
    uncontacted: truthy((r, ctx) => isUncontactedChoice(r as unknown as Quote, ctx.db)),
    companyOid: (r, v) => (r['offers'] as { companyOid: string }[]).some(o => o.companyOid === v),
  },
  claims: {
    open: (r, v) => (v === 'open') === (r['open'] === true),
    deadline: truthy((r, ctx) => r['open'] === true
      && String(r['legalDeadlineDate']) <= addDays(ctx.today, CLAIM_DEADLINE_WARN_DAYS)),
    stale: truthy(r => r['open'] === true && (r['daysWithoutNews'] as number) >= CLAIM_STALE_NEWS_DAYS),
  },
  tickets: {
    overdue: truthy(r => r['overdue'] === true),
    active: truthy(r => r['active'] === true),
    due: (r, v, ctx) => r['active'] === true && v === 'today' && r['dueDate'] === ctx.today,
    view: (r, v, ctx) => {
      const me = ctx.user?.memberOid;
      if (v === 'assigned') return r['assigneeOid'] === me;
      if (v === 'escalated') return r['assigneeOid'] === me && r['escalated'] === true;
      if (v === 'created') return r['creatorOid'] === me;
      return true;
    },
  },
  installments: {
    status: (r, v) => (v === 'on-time' ? ['paid', 'pending'].includes(String(r['displayStatus'])) : r['displayStatus'] === v),
    paymentState: (r, v) => (r['paymentMethod'] as { state: string }).state === v,
    due: (r, v, ctx) => {
      if (r['status'] === 'paid') return false;
      const due = String(r['dueDate']);
      if (v === 'overdue') return due < ctx.today;
      return due >= ctx.today && due <= addDays(ctx.today, Number(v));
    },
  },
  notifications: {
    unread: truthy(r => r['read'] === false),
  },
  endorsements: {
    retroactive: truthy(r => r['retroactive'] === true),
  },
  documents: {
    entity: (r, v) => (r['entityTypes'] as string[]).includes(v),
  },
  'commission-lines': {
    line: (r, v) => r['lineOfBusiness'] === v,
    producerOid: (r, v, ctx) => subtreeOf(ctx, v).has(String(r['ownerOid'])),
    onlyDiff: truthy(r => r['difference'] !== null && Math.abs(r['difference'] as number) >= COMMISSION_TOLERANCE),
  },
};

const DATE_FIELD: Record<string, string> = {
  policies: 'startDate', quotes: 'createDate', claims: 'reportedDate', installments: 'dueDate',
  tickets: 'dueDate', 'commission-lines': 'paidDate', documents: 'date',
};

/** Filters every dashboard link may carry: owner subtree and an inclusive date window on the collection's main date. */
function commonPredicate(name: string, key: string): Predicate | undefined {
  const field = DATE_FIELD[name];
  if (key === 'producerOid') return (r, v, ctx) => subtreeOf(ctx, v).has(String(r['ownerOid']));
  if (key === 'from' && field) return (r, v) => r[field] !== null && String(r[field]) >= v;
  if (key === 'to' && field) return (r, v) => r[field] !== null && String(r[field]) <= v;
  return undefined;
}

export function matchesFilters(name: string, row: unknown, filters: Record<string, string>, ctx: ProjectionContext): boolean {
  const r = row as Row;
  return Object.entries(filters).every(([key, value]) => {
    if (value === '') return true;
    const predicate = PREDICATES[name]?.[key] ?? commonPredicate(name, key);
    return predicate ? predicate(r, value, ctx) : String(r[key]) === value;
  });
}

const BY: Record<string, (a: Row, b: Row) => number> = {
  persons: (a, b) => String(a['displayName']).localeCompare(String(b['displayName'])),
  quotes: (a, b) => String(b['createDate']).localeCompare(String(a['createDate'])),
  claims: (a, b) => String(b['reportedDate']).localeCompare(String(a['reportedDate'])),
  tickets: (a, b) => String(a['dueDate'] ?? '9999').localeCompare(String(b['dueDate'] ?? '9999')),
  installments: (a, b) => String(a['dueDate']).localeCompare(String(b['dueDate'])),
  policies: (a, b) => String(a['endDate']).localeCompare(String(b['endDate'])),
  endorsements: (a, b) => String(b['registerDate']).localeCompare(String(a['registerDate'])),
  communications: (a, b) => String(b['date']).localeCompare(String(a['date'])),
  documents: (a, b) => String(b['date']).localeCompare(String(a['date'])),
  'unusual-reports': (a, b) => String(b['operationDate']).localeCompare(String(a['operationDate'])),
  'commission-lines': (a, b) => String(b['paidDate']).localeCompare(String(a['paidDate'])),
  settlements: (a, b) => String(b['period']).localeCompare(String(a['period'])) || String(a['companyName']).localeCompare(String(b['companyName'])),
};

export function sortRows<T>(name: string, rows: T[]): T[] {
  const compare = BY[name];
  return compare ? [...rows].sort((a, b) => compare(a as Row, b as Row)) : rows;
}

export function summarizeInstallments(rows: InstallmentRow[], visiblePolicies: Policy[]): InstallmentsSummary {
  const bucket = (test: (r: InstallmentRow) => boolean) => {
    const hits = rows.filter(test);
    return { count: hits.length, amount: hits.reduce((sum, r) => sum + r.amount, 0) };
  };
  const active = visiblePolicies.filter(p => p.status === 'active');
  const withInstallments = active.filter(p => p.installmentsLoaded).length;
  return {
    upToDate: bucket(r => r.displayStatus === 'paid' || r.displayStatus === 'pending'),
    overdue: bucket(r => r.displayStatus === 'overdue'),
    mora: bucket(r => r.displayStatus === 'mora'),
    policiesTotal: active.length,
    policiesWithInstallments: withInstallments,
    policiesWithoutInstallments: active.length - withInstallments,
    installmentsTotal: rows.length,
    paymentMethodUnknown: rows.filter(r => r.paymentMethod.state !== 'known').length,
  };
}
